import { Box, Icon, Skeleton, Stack, Typography } from '@mui/material';
import { DATE_FORMAT_TIME_FNS, SyncUser, ThreadUser, WithAvatar, WithName } from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useFlag } from '@schooly/hooks/use-flag';
import { CrossCircleIcon } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { getUserFullName } from '@schooly/utils/get-user-full-name';
import { format, parseISO } from 'date-fns';
import React, { FC, PropsWithChildren, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { DefaultGenerics } from 'stream-chat';
import {
  DefaultStreamChatGenerics,
  EventComponentProps,
  MessageContextValue,
  StreamMessage,
  useDeleteHandler,
  useMessageContext,
} from 'stream-chat-react';

import { useThreads } from '../ThreadContext';
import { MessageActions, useMessageActions } from './MessageActions';

const AVATAR_SIZE = 38;

export enum SystemMessageType {
  MEMBER_JOINED = 'member_joined',
}

export type MemberJoinedMessage = {
  type: SystemMessageType.MEMBER_JOINED;
  joinedMembers: ThreadUser[];
};

export type SystemMessage = StreamMessage<DefaultGenerics> & {
  data?: MemberJoinedMessage;
};

type MessageProps = {
  onDelete?: (message: StreamMessage<DefaultStreamChatGenerics>) => void;
  onEdit?: (message: StreamMessage<DefaultStreamChatGenerics>) => void;
} & Partial<MessageContextValue<DefaultGenerics>>;

export const Message = ({ onDelete, onEdit }: MessageProps) => {
  const { isMyMessage: MyMessage, message } = useMessageContext();
  const deleteHandler = useDeleteHandler(message);
  const isMyMessage = MyMessage();
  const { getConfirmation } = useConfirmationDialog();
  const isDeleted = message.type === 'deleted';
  const { formatMessage } = useIntl();

  const formatMessageTime = (time: string | Date) => {
    return format(
      typeof time === 'string' ? newDateTimezoneOffset(time) : time,
      DATE_FORMAT_TIME_FNS,
    );
  };

  const messageTime = message.created_at ? formatMessageTime(message.created_at) : null;

  const messageDeletedTime = message.deleted_at ? formatMessageTime(message.deleted_at) : null;

  const messageEditedTime =
    typeof message.edited_at === 'string' ? formatMessageTime(parseISO(message.edited_at)) : null;

  const handleDelete = async (
    message: StreamMessage<DefaultStreamChatGenerics>,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const isConfirmed = await getConfirmation({
      textId: 'messages-DeleteMessageConfirmationHeader',
      confirmTextId: 'action-Delete',
      content: (
        <Typography variant="h1" color="common.grey3" pt={1}>
          {formatMessage({ id: 'messages-DeleteMessageConfirmationContent' })}
        </Typography>
      ),
    });

    if (isConfirmed) {
      await deleteHandler(e);
      onDelete?.(message);
    }
  };

  const handleCopy = async (message: StreamMessage<DefaultStreamChatGenerics>) => {
    if (message.text) {
      await navigator.clipboard.writeText(message.text);
    }
  };

  const canShowActions = isMyMessage && !isDeleted;

  const actions = useMessageActions(
    message,
    canShowActions ? { onEdit, onCopy: handleCopy, onDelete: (e) => handleDelete(message, e) } : {},
  );

  if (!message.user) {
    return null;
  }

  return (
    <Stack data-message-id={message.id} mb={2}>
      <MessageContent
        isMyMessage={isMyMessage}
        title={
          <MessageTitle
            isMyMessage={isMyMessage}
            userName={
              isMyMessage
                ? formatMessage({ id: 'messages-You' })
                : getUserFullName(message.user as unknown as SyncUser)
            }
            isEdited={!!messageEditedTime && !isDeleted}
            messageTime={messageDeletedTime ?? messageEditedTime ?? messageTime}
          />
        }
        isDeleted={isDeleted}
        text={message.text ? message.text.trim() : ''}
        user={message.user as unknown as SyncUser}
        messageActions={(isHovered, onClose) => (
          <MessageActions actions={actions} isHovered={isHovered} onClose={onClose} />
        )}
      />
    </Stack>
  );
};

export const MessageTitle = ({
  isMyMessage,
  userName,
  messageTime,
  isEdited,
}: {
  isMyMessage: boolean;
  userName: ReactNode;
  messageTime: ReactNode;
  isEdited?: boolean;
}) => {
  return (
    <Stack direction="row" alignItems="center" gap={0.75}>
      {!isMyMessage ? (
        <>
          <Typography variant="h3" color="common.grey2">
            {userName}
          </Typography>
          <Typography variant="caption" color="common.grey3">
            {messageTime}
          </Typography>
          {isEdited && <EditedLabel isMyMessage={isMyMessage} />}
        </>
      ) : (
        <>
          {isEdited && <EditedLabel isMyMessage={isMyMessage} />}
          <Typography variant="caption" color="common.grey3">
            {messageTime}
          </Typography>
          <Typography variant="h3" color="common.grey2">
            {userName}
          </Typography>
        </>
      )}
    </Stack>
  );
};

type MessageContentProps = {
  isMyMessage?: boolean;
  title?: ReactNode;
  text?: string;
  user?: WithName & WithAvatar;
  isLoading?: boolean;
  maxWidth?: string;
  isDeleted?: boolean;
  minWidth?: string;
  messageActions?: (isHovered: boolean, onClose: () => void) => ReactNode;
} & PropsWithChildren;

export const MessageContent: FC<MessageContentProps> = ({
  isMyMessage,
  title,
  children,
  text,
  user,
  isLoading,
  maxWidth = '88%',
  isDeleted = false,
  minWidth,
  messageActions,
}) => {
  const userAvatar = !!user ? (
    <AvatarAuth user={user} sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }} />
  ) : null;
  const { formatMessage } = useIntl();
  const [hovered, onMouseEnter, onMouseLeave] = useFlag(false);

  const alignItems = isMyMessage ? 'flex-end' : 'flex-start';

  const textColor = isMyMessage ? 'primary.main' : 'common.grey2';

  const myMessageBgColor =
    messageActions && hovered && !isDeleted ? 'common.grey6' : 'common.grey7';

  const deletedMessage = isDeleted ? (
    <Stack direction="row" gap={0.5} alignItems="center">
      <Icon fontSize="small" sx={{ color: 'common.grey', pr: 0.5 }}>
        <CrossCircleIcon />
      </Icon>
      {formatMessage({
        id: isMyMessage
          ? 'messages-ThreadChannelOwnMessageDeleted'
          : 'messages-ThreadChannelMessageDeleted',
      })}
    </Stack>
  ) : null;

  return (
    <Stack
      sx={{
        alignItems,
        alignSelf: alignItems,
        maxWidth,
      }}
    >
      {!isLoading ? (
        <Stack
          sx={{
            flexDirection: 'row',
            pl: 0,
            ml: !isMyMessage ? 7 : 0,
          }}
        >
          {title}
        </Stack>
      ) : null}

      <Stack
        direction="row"
        gap={1.5}
        sx={{
          alignSelf: alignItems,
        }}
      >
        {!isMyMessage && (
          <Box>
            {isLoading ? (
              <Skeleton
                variant="circular"
                width={AVATAR_SIZE}
                height={AVATAR_SIZE}
                sx={{
                  visibility: user ? 'visible' : 'hidden',
                }}
              />
            ) : (
              userAvatar
            )}
          </Box>
        )}

        <Stack
          sx={(theme) => ({
            backgroundColor: isMyMessage ? myMessageBgColor : theme.palette.background.paper,
            borderRadius: theme.spacing(2),
            p: 1,
            height: '100%',
            wordBreak: 'break-word',
            minWidth,
            position: 'relative',
          })}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {isLoading ? (
            <Stack spacing={0.5} p={1}>
              <Skeleton variant="rectangular" width="90%" height={12} />
              <Skeleton variant="rectangular" width="76%" height={12} />
            </Stack>
          ) : (
            <>
              <Typography
                variant={'h3'}
                color={isDeleted ? 'common.grey' : textColor}
                whiteSpace="pre-wrap"
              >
                {deletedMessage ?? text ?? ''}
              </Typography>
              {messageActions?.(hovered, onMouseLeave)}
            </>
          )}
        </Stack>

        {children}
      </Stack>
    </Stack>
  );
};

const EditedLabel: FC<{
  isMyMessage: boolean;
}> = ({ isMyMessage }) => {
  const { formatMessage } = useIntl();
  return (
    <Stack direction={isMyMessage ? 'row' : 'row-reverse'} alignItems="center" gap={0.75}>
      <Typography variant="caption" color="common.grey3">
        {formatMessage({ id: 'messages-Edited' })}
      </Typography>
      <Box
        sx={{
          width: '2px',
          height: '2px',
          backgroundColor: 'common.grey3',
          borderRadius: '50%',
        }}
      />
    </Stack>
  );
};

export const MessageSystem: React.FC<EventComponentProps<DefaultGenerics>> = ({ message }) => {
  const systemMessage = message as SystemMessage;
  const { connectedUser } = useThreads();
  const { formatMessage } = useIntl();

  if (!connectedUser || systemMessage.data?.type !== SystemMessageType.MEMBER_JOINED) return null;

  // For now, we only support one member joining at a time
  const member = systemMessage.data.joinedMembers?.[0];
  if (!member) return null;

  return (
    <Stack justifyContent="center" alignItems="center" pt={0.25} pb={2}>
      <Typography color="common.grey2">
        {member.user_id === connectedUser.user_id
          ? formatMessage({ id: 'messages-YouHaveJoinedTheChat' })
          : formatMessage(
              {
                id: 'messages-HasJoinedTheChat',
              },
              { user: getUserFullName(member) },
            )}
      </Typography>
    </Stack>
  );
};
